import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { EnvelopeIcon, LockClosedIcon, Cog8ToothIcon, BellAlertIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'

import GradientButton from 'src/components/buttons/GradientButton'
import GradientWrapper from 'src/components/wrappers/GradientWrraper'
import { selectEmail } from 'src/store/auth'

import ChangePasswordDialog from './components/ChangePasswordDialog'
import SmtpNotification from './components/SmtpNotification'
import TelegramNotification from './components/TelegramNotification'
import TwoFASetting from './components/TwoFASetting'

function SettingsPage() {
  const [open, setOpen] = useState(false)
  const email = useSelector(selectEmail)
  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  const [section, setSection] = useState<'profile' | 'security' | 'notifications'>('profile')

  return (
    <div className="flex h-screen w-full bg-[#141414] text-gray-200 overflow-hidden">
      {/* === Left Sidebar === */}
      <aside className="fixed left-0 top-0 bottom-0 w-60 bg-[#0d0d0d] border-r border-[#222] p-5 flex flex-col">
        <div className="text-lg font-semibold mb-8 text-white tracking-wide">Settings</div>

        <nav className="flex flex-col gap-1">
          {[
            { key: 'profile', label: 'Profile', icon: EnvelopeIcon },
            { key: 'security', label: 'Security', icon: Cog8ToothIcon },
            { key: 'notifications', label: 'Notifications', icon: BellAlertIcon },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSection(key as any)}
              className={`flex items-center gap-2 text-left px-3 py-2 rounded-md transition ${
                section === key
                  ? 'bg-[#1b1b1b] text-white'
                  : 'hover:bg-[#161616] text-gray-400'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* === Main Content === */}
      <main
        className="flex flex-col flex-1 ml-60 h-full overflow-y-auto px-10 py-10"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          main::-webkit-scrollbar { display: none; }
        `}</style>

        {section === 'profile' && (
          <section className="w-full max-w-full overflow-x-hidden">
            

            <GradientWrapper
              border={1}
              gradientOpacity={0.3}
              className="rounded-lg bg-[#1a1a1a] p-6 shadow-sm border border-[#2a2a2a]"
            >
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-2">
                    <EnvelopeIcon className="text-gray-400" width={18} />
                    <div className="font-semibold text-white">Email</div>
                  </div>
                  <div className="text-gray-400 md:pl-6 text-sm">
                    This email is used to log in and receive notifications.
                  </div>
                </div>
                <div className="text-white text-sm font-medium break-all">{email}</div>
              </div>
            </GradientWrapper>
          </section>
        )}

        {section === 'security' && (
          <section className="w-full max-w-full overflow-x-hidden">
            

            <GradientWrapper
              border={1}
              gradientOpacity={0.3}
              className="rounded-lg bg-[#1a1a1a] p-6 shadow-sm border border-[#2a2a2a]"
            >
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-2">
                    <LockClosedIcon className="text-gray-400" width={18} />
                    <div className="font-semibold text-white">Login Password</div>
                  </div>
                  <div className="text-gray-400 md:pl-6 text-sm">
                    Used to access your account securely.
                  </div>
                </div>

                <div>
                  <GradientButton size="sm" className="rounded-md" onClick={handleOpen}>
                    Change
                  </GradientButton>
                </div>

                <ChangePasswordDialog open={open} onClose={handleClose} />
              </div>

              <TwoFASetting className="mt-8" />
            </GradientWrapper>
          </section>
        )}

        {section === 'notifications' && (
          <section className="w-full max-w-full overflow-x-hidden">
            

            <GradientWrapper
              border={1}
              gradientOpacity={0.3}
              className="rounded-lg bg-[#1a1a1a] p-6 shadow-sm border border-[#2a2a2a]"
            >
              <SmtpNotification className="mt-6" />
              <TelegramNotification className="mt-6" />
            </GradientWrapper>
          </section>
        )}
      </main>
    </div>
  )
}

export default SettingsPage
